<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="450">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="submitForm">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4 v-if="record" class="text-capitalize">Update "{{ record.name }}"</h4>
                    <h4 v-else class="text-capitalize">New {{ type }} Service Type</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12">
                            <company-select v-if="user.type === 'admin'"
                                      v-model="formData['company_id']"
                                      :items="companies"
                                      clearable
                                      density="compact" label="Company">
                            </company-select>

                            <v-text-field v-model="formData['name']" :rules="[vRules.required,vRules.maxLength(100)]"
                                          autofocus density="compact" label="Name"/>
                            <v-switch v-model="formData['recurring']" color="primary" density="compact"
                                      label="Recurring"/>

                            <v-text-field v-model="formData['frequency_days']" density="compact"
                                          label="Frequency Days" type="number"
                                          :hint="isInspection ? 'A scheduled inspection of this type repeats this often, unless the order sets its own.' : ''"
                                          :persistent-hint="isInspection"/>
                            <v-text-field v-model="formData['frequency_miles']" density="compact"
                                          label="Frequency Miles" type="number"
                                          hint="Not used for scheduling yet." persistent-hint/>
                            <v-switch v-if="isInspection" v-model="formData['completed_by_driver_inspection']"
                                      color="primary" density="compact"
                                      hint="A driver's pre-trip or post-trip inspection (DVIR) on the vehicle completes a scheduled one of this type once it is due."
                                      label="Completed by a driver's inspection (DVIR)" persistent-hint/>
                            <v-switch v-model="formData['reminder_needed']" color="primary" density="compact"
                                      label="Reminder Needed"/>
                            <v-switch v-model="formData['applies_to_all']" color="primary" density="compact"
                                      label="Applies to All Vehicle Types"/>

                            <v-textarea v-model="formData['description']" density="compact" label="Description"
                                        maxlength="255"/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useAuthStore} from "@/stores/authStore";
import CompanySelect from "@/components/globals/company-select.vue";

const snackbar = useSnackbarStore();
const {user} = useAuthStore();
const props = defineProps({
    modelValue: Boolean,
    record: Object,
    type: String,
    serviceType: Number,
});
const formData = reactive({
    valid: false,
    _method: props.record ? 'put' : 'post',
    name: props.record?.name ?? null,
    type: props.record?.type ?? props.serviceType,
    description: props.record?.description ?? null,
    recurring: !!props.record?.recurring ?? true,
    frequency_days: props.record?.frequency_days ?? null,
    frequency_miles: props.record?.frequency_miles ?? null,
    reminder_needed: !!props.record?.reminder_needed ?? true,
    applies_to_all: !!props.record?.applies_to_all ?? true,
    completed_by_driver_inspection: !!props.record?.completed_by_driver_inspection,
    company_id: props.record ? props.record?.company_id : ((user.type !== 'admin') ? user.company.id : null),
});
const emits = defineEmits(['update:modelValue', 'addRecord', 'updateRecord']);
const isInspection = (props.record?.type ?? props.serviceType) === 1;
const dialogSwitch = ref(props.modelValue);
const companies = ref([]);
const errors = ref([]);

onMounted(() => {
    if (user.type === 'admin') {
        fetchCompanies()
    }
})

const fetchCompanies = () => {
    send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            snackbar.showSnackbar('Companies could not be loaded', 'error')
        })
}
const submitForm = () => {
    errors.value = [];
    send('post', `fleet/service-types${props.record ? '/' + props.record.id : ''}`, formData).then((response) => {
        snackbar.showSnackbar(response.data.message);
        if (response.data.success) {
            emits(props.record ? 'updateRecord' : 'addRecord', response.data.data);
            emits('update:modelValue', false);
        }
    }).catch(function (errorResponse) {
        if (errorResponse.response?.data?.errors) {
            let validationErrors = errorResponse.response.data.errors;
            Object.keys(validationErrors).forEach((key) => {
                errors.value.push(validationErrors[key][0]);
            });
        } else if (errorResponse.response?.data.message) {
            errors.value.push(errorResponse.response.data.message);
        }
        snackbar.showSnackbar('Something went wrong', 'error');
        document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
    })
}

</script>
